import { CHART_MAX_YEAR, METRIC_FORK_YEAR, type ComparisonPlanId, type MetricDetail, type MetricId } from "./metricDetailsTypes"
import { CHART_SERIES } from "./metricChartData"
import { METRIC_LABELS } from "./metricLabels"

// Draw order for the plans drawn against Plan A on the explainer chart, all
// in the default path's red dashed line design, identified by the lettered
// tag at the line end. D comes from each metric's own series; B/C/S from the
// model spreadsheet extract in planBranchSeries.
export const COMPARISON_PLAN_ORDER: ComparisonPlanId[] = ["D", "B", "C", "S"]

// Actual supplement slugs, linkable under /supplements/<slug>. Model- and
// observed-data provenance is described in the explanation prose instead, so it
// never appears as a fake "source" chip.
export const SUPPLEMENT_SLUGS = new Set<string>([
  "economics-of-plan-a",
  "compute-supplement",
  "capability-scaling-strategy",
  "transparency-plan",
  "how-plan-a-solves-each-of-the-problems",
])

// Human-readable titles as shown in the app's supplements list (not the slug).
export const SUPPLEMENT_TITLES: Record<string, string> = {
  "economics-of-plan-a": "Economics of Plan A",
  "compute-supplement": "Compute in Plan A",
  "capability-scaling-strategy": "Capability Scaling Strategy",
  "transparency-plan": "Transparency Plan",
  "how-plan-a-solves-each-of-the-problems": "How Plan A solves each of the problems",
}

// One entry per wired metric: config (axis/labels/split) and prose. The numeric
// chart series live in metricChartData.csv and are wired in via CHART_SERIES.
export const METRIC_DETAILS: Partial<Record<MetricId, MetricDetail>> = {
  labor: {
    id: "labor",
    label: METRIC_LABELS.labor,
    axis: {
      type: "log",
      min: 1e7,
      max: 1e14,
      unit: "humanEquiv",
      ticks: [
        { value: 1e13, label: "10T" },
        { value: 1e11, label: "100B" },
        { value: 1e9, label: "1B" },
        { value: 1e7, label: "10M" },
      ],
    },
    xTicks: [2024, 2025, METRIC_FORK_YEAR, 2033, CHART_MAX_YEAR],
    splitYear: METRIC_FORK_YEAR,
    series: CHART_SERIES.labor,
    explanation:
      "Machine labor grows with the compute buildout and with falling inference costs — each doubling of compute, or halving of cost, doubles the machine workforce.",
    scenarios: {
      A: "Limitations on both algorithmic progress and (eventually) compute buildouts lead to a much slower increase, although by 2040 the amount of AI cognitive labor is much greater than that of humans.",
      D: "An intelligence explosion causes very fast growth in the number and quality of AIs.",
    },
    sources: ["economics-of-plan-a"],
  },

  income: {
    id: "income",
    label: METRIC_LABELS.income,
    axis: {
      type: "log",
      min: 4e4,
      max: 2e7,
      ticks: [
        { value: 1e7, label: "$10M" },
        { value: 1e6, label: "$1M" },
        { value: 1e5, label: "$100K" },
      ],
    },
    xTicks: [2024, 2025, METRIC_FORK_YEAR, 2033, CHART_MAX_YEAR],
    splitYear: METRIC_FORK_YEAR,
    series: CHART_SERIES.income,
    explanation:
      "Wages stop carrying the median household once machine labor undercuts them; whether incomes then stagnate or explode comes down to transfers like the citizen’s dividend.",
    scenarios: {
      A: "AI-driven economic growth enables a massive citizen’s dividend, causing widespread wealth and abundance.",
      D: "An intelligence explosion quickly leads to AIs automating the whole economy. They can do all tasks a human can for much cheaper, and so wages fall quickly.",
    },
    sources: ["economics-of-plan-a"],
  },

  employment: {
    id: "employment",
    label: METRIC_LABELS.employment,
    axis: { type: "linear", topLabel: "100%" },
    xTicks: [2024, 2025, METRIC_FORK_YEAR, 2033, CHART_MAX_YEAR],
    splitYear: METRIC_FORK_YEAR,
    series: CHART_SERIES.employment,
    explanation:
      "As machine labor gets cheaper than people at more and more tasks, wages fall and workers leave paid work.",
    scenarios: {
      A: "The limitations on algorithmic progress prevent an intelligence explosion from quickly reaching superintelligence. Furthermore, cap-and-trade on robots and compute production limits the speed at which AIs can automate the economy. The result is AIs with capabilities in the human range (but advantages in cost and speed) gradually automating the economy over the course of the 2030s.",
      D: "An intelligence explosion occurs, resulting in vastly superhuman AIs, which quickly automate the entire economy.",
    },
    sources: ["economics-of-plan-a"],
  },

  safetyTax: {
    id: "safetyTax",
    label: METRIC_LABELS.safetyTax,
    axis: { type: "linear", max: 10, topLabel: "10 yr", unit: "years" },
    xTicks: [2024, 2025, METRIC_FORK_YEAR, 2033, CHART_MAX_YEAR],
    splitYear: METRIC_FORK_YEAR,
    series: CHART_SERIES.safetyTax,
    explanation:
      "Doing AI development safely costs time. The slowdown is how much later capabilities arrive than on an unconstrained path — time bought to make sure the AIs we hand off to are aligned.",
    scenarios: {
      A: "Safer training, restricted R&D, and capped production add up to roughly a decade of accumulated delay by the hand-off.",
      D: "Race pressure erodes even today’s small voluntary delay to zero; AIs are deployed as fast as they are built.",
    },
    sources: ["capability-scaling-strategy"],
  },

  alignment: {
    id: "alignment",
    label: METRIC_LABELS.alignment,
    axis: {
      type: "log",
      min: 1000,
      max: 300000,
      unit: "count",
      ticks: [
        { value: 300000, label: "300K" },
        { value: 100000, label: "100K" },
        { value: 10000, label: "10K" },
        { value: 1000, label: "1K" },
      ],
    },
    xTicks: [2024, 2025, METRIC_FORK_YEAR, 2033, CHART_MAX_YEAR],
    splitYear: METRIC_FORK_YEAR,
    series: CHART_SERIES.alignment,
    explanation:
      "The binding constraint is access, not talent: only researchers who can run experiments on frontier models count.",
    scenarios: {
      A: "Broad access to frontier AI — more researchers have more time to work on AI alignment.",
      D: "Only AI researchers in AI companies have access to frontier AI, so the researcher pool is limited.",
    },
    sources: ["transparency-plan"],
  },

  capability: {
    id: "capability",
    label: METRIC_LABELS.capability,
    axis: {
      type: "log",
      min: 1,
      max: 200000,
      unit: "multiplier",
      ticks: [
        { value: 100000, label: "100k×" },
        { value: 1000, label: "1k×" },
        { value: 100, label: "100×" },
        { value: 10, label: "10×" },
        { value: 1, label: "1×" },
      ],
    },
    xTicks: [2024, 2025, METRIC_FORK_YEAR, 2033, CHART_MAX_YEAR],
    splitYear: METRIC_FORK_YEAR,
    series: CHART_SERIES.capability,
    explanation:
      "Capability climbs far more slowly than compute — each step up the milestone ladder costs orders of magnitude more effective compute.",
    scenarios: {
      A: "Limitations on both algorithmic progress and (eventually) compute buildouts slow down AI capabilities. AI companies scale to top-expert-level AI from 2030–2035, and then pause there until 2040.",
      D: "An intelligence explosion causes a rapid increase in AI capabilities.",
    },
    sources: ["capability-scaling-strategy", "compute-supplement"],
  },

  power: {
    id: "power",
    label: METRIC_LABELS.power,
    axis: {
      type: "log",
      // Floor at 3%: the live series (largest single frontier holder) starts
      // near the observed ~3.3% in 2025 before climbing to ~8% in 2026 and
      // ~30% by 2040, so the floor hugs that opening reading rather than
      // clipping it off the bottom of the plot.
      min: 0.03,
      max: 0.6,
      unit: "percent",
      ticks: [
        { value: 0.5, label: "50%" },
        { value: 0.2, label: "20%" },
        { value: 0.1, label: "10%" },
        { value: 0.05, label: "5%" },
      ],
    },
    xTicks: [2024, 2025, METRIC_FORK_YEAR, 2033, CHART_MAX_YEAR],
    splitYear: METRIC_FORK_YEAR,
    // Getter defers the share derivation to first access; the details panel
    // normally uses the live hook (usePowerShareSeries) instead.
    get series() {
      return CHART_SERIES.power
    },
    explanation:
      "Power increasingly comes from controlling the best AIs, so we proxy its concentration by the largest single holder’s share of world frontier compute. A falling share means power is spreading out.",
    scenarios: {
      A: "Total Research Transparency publishes the core algorithms, evaporating the leaders’ moat and letting other companies and countries catch up. The largest holder’s share stops climbing.",
      D: "Algorithmic secrets remain the leaders’ moat and money flows to whoever controls the best AIs, so compute keeps pooling — by 2035 a handful of firms in one country control the frontier.",
    },
    sources: ["transparency-plan", "compute-supplement"],
  },
}
